/**
 * SW-1 — the universal property switcher.
 *
 * A header control that names the property you are looking at and lets you
 * move to the same view of another one, from anywhere in the app.
 *
 * **What this deliberately is not.** "Universal property switcher" can mean a
 * global filter that scopes every page — dashboards, reports, accounting — to
 * one property. That reading is a much larger change and a riskier one: a
 * persistent, easily-forgotten filter sitting above financial statements is
 * how somebody reads a single property's NOI as the portfolio's. This is the
 * navigational reading: it changes *which* property you are looking at, never
 * what a number means. A `PropertyContext` can be layered on later if the
 * scoping reading is wanted; nothing here forecloses it.
 *
 * **Filtering is server-side.** `/api/properties/lookup` caps what it returns,
 * so filtering the fetched array in the browser would filter an already
 * truncated list and silently hide properties from whoever typed a name.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Building2, Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { propertyIdFromPath } from '../lib/propertyPath';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEBOUNCE_MS = 200;

export default function PropertySwitcher() {
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);
  const [currentLabel, setCurrentLabel] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const currentId = propertyIdFromPath(location.pathname)
    ?? (params.id ? Number(params.id) : null);

  const fetchRows = useCallback(async (q) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/properties/lookup`, {
        params: q ? { q } : {},
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
      setActive(0);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      setRows([]);
      setError('Could not load properties.');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [token]);

  // Name the property in the button even on a deep link, where the list has
  // never been opened. One call, only when we do not already know the name.
  useEffect(() => {
    if (!token || currentId == null) { setCurrentLabel(null); return; }
    const known = rows.find((r) => r.id === currentId);
    if (known) { setCurrentLabel(known.label); return; }
    let cancelled = false;
    axios.get(`${API_URL}/api/properties/lookup`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (cancelled) return;
      const hit = (res.data || []).find((r) => r.id === currentId);
      if (hit) setCurrentLabel(hit.label);
    }).catch(() => { /* the button falls back to the id */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentId]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => fetchRows(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [open, term, fetchRows]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const choose = useCallback((row) => {
    if (!row) return;
    setOpen(false);
    // Keep the same view of the new property when the URL names one.
    const tab = new URLSearchParams(location.search).get('tab');
    navigate(`/properties/${row.id}${tab ? `?tab=${encodeURIComponent(tab)}` : ''}`);
  }, [navigate, location.search]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (rows.length ? (i + 1) % rows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(rows[active]);
    }
  };

  // Server-side gates decide what the list contains; this only decides
  // whether offering the control makes sense at all.
  if (!hasPermission('properties:view_all')) return null;

  const buttonLabel = currentId == null
    ? 'All properties'
    : (currentLabel || `Property #${currentId}`);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setTerm(''); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 max-w-[14rem] px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"
        data-testid="property-switcher-button"
      >
        <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <span className="truncate">{buttonLabel}</span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 mt-1 w-80 max-w-[80vw] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden z-50"
          data-testid="property-switcher-menu"
        >
          <div className="flex items-center gap-2 px-3 border-b border-slate-200 dark:border-slate-700">
            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Find a property…"
              aria-label="Find a property"
              className="flex-1 h-10 bg-transparent text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none"
              data-testid="property-switcher-input"
            />
            {loading && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {error && (
              <p className="px-3 py-4 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
            {!error && !loading && rows.length === 0 && (
              <p className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">
                {term.trim() ? `No property matches “${term.trim()}”.` : 'No properties yet.'}
              </p>
            )}
            {rows.map((row, i) => (
              <button
                key={row.id}
                type="button"
                role="option"
                aria-selected={row.id === currentId}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(row)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                  i === active
                    ? 'bg-slate-100 dark:bg-slate-700'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
                data-testid={`property-switcher-option-${row.id}`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-slate-900 dark:text-white truncate">
                    {row.label}
                  </span>
                  {row.sublabel && (
                    <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                      {row.sublabel}
                    </span>
                  )}
                </span>
                {row.id === currentId && (
                  <Check className="w-4 h-4 text-slate-400 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
